// apps/merchant-v3/src/router.tsx
//
// Phase 6 Wave 1 Task 02 — Route map for the merchant portal SPA.
// React Router v7 history mode (clean paths — no hashes).
// All route components are lazy-loaded so each page ships a separate
// chunk (code splitting). SPA fallback `*` → 404 page.
//
// Phase 6 Wave 1 Task 03 — Auth guard wired. Public routes (/login,
// /forgot-password) bypass the guard; every other route is wrapped
// with <RequireAuth> which redirects to /login?returnUrl=… on 401
// (deep-link preservation).
//
// Phase 6 Wave 3 Task 08 — `/products/:id` now resolves to the rich
// BlueprintDetailPage (replaces the Wave 1 placeholder).
//
// Phase 6 Wave 3 Task 10 — `/products/my/:id` now resolves to
// MyProductEditor (the merchant-side product detail / edit page).
//
// Phase 6 Wave 4 Task 11 — `/products/create` now resolves to the new
// 5-step ProductCreateWizard shell (Step 1 Blueprint Selection +
// Step 1.5 Stores Override land in this task; Steps 2-5 stub-loaded
// behind a placeholder until Tasks 12 / 13). The wizard owns its own
// step routing via `?step=…` so we keep the public URL `/products/create`
// stable for deep-links.
//
// Phase 6 Wave 4 Task 14 — `/products/settings` resolves to
// ProductSettingsPage (merchant defaults + notification prefs).
//
// Phase 6 Wave 5 Task 15 — `/artwork` (canonical) and the legacy
// `/products/artwork` both resolve to the new `ArtworkLibraryPage`
// (file manager + folder tree + lightbox + upload zone). The legacy
// alias is kept so the Sidebar group "Products → Artwork Library"
// link continues to work without a coordinated nav change.
//
// Phase 6 Wave 5 Task 16 — `/artwork/:id/edit` resolves to the new
// `ArtworkEditorPage` (basic crop / rotate / resize editor — full
// Design Editor with Fabric.js deferred to Milestone 2).
//
// Phase 6 Wave 6 Task 17 — `/orders` now resolves to the new
// `OrdersListPage` (replaces the Wave 1 placeholder). Provides the
// 7 status tabs, multi-field search, SharedDataTable wrapper with
// column presets + SLA badge, bulk-action toolbar (cancel + CSV
// export), and the WS live-update banner ("X new orders") wired
// against the `merchant.${id}.orders` channel (
//
// Phase 6 Wave 6 Task 18 — `/orders/:id` now resolves to the new
// `OrderDetailPage` (replaces the Wave 1 placeholder). Provides the
// 70/30 layout with 3 tabs (Overview / Activity / Tracking) + sticky
// cost-breakdown sidebar, vendor-anonymized activity timeline, per-
// item tracking cards, cancel-with-wallet-refund modal and packing-
// list PDF export (
//
// Phase 6 Wave 6 Task 19 / rebuilt 2026-09-25 — `/orders/create` resolves
// to `OrderCreatePage`, which is STEP 2 (variant selection) of order
// creation. Step 1 (product selection) is a modal on the orders list, so
// this route is entered by picking products there and pressing "Start
// Order"; the picks travel in the wizard store's `selectedProducts`, and
// the page redirects back to `/orders` when that slot is empty.
//
// This replaced `OrderCreateWizard` — the 3-step modal wizard with its own
// internal `?step=` routing — which was removed along with its
// `StepProducts` picker rather than adapted. The `?step=payment` surface it
// used to host is gone with it; `OrderPaymentPage` is still reachable from
// the order-detail "Pay now" modal (
//
// Phase 6 Wave 6 Task 20 — `/orders/import` now resolves to the new
// `ExcelImportPage` (replaces the Wave 1 placeholder). 5-step wizard:
// Upload → Matching → Review → Payment → Result. Single batch payment
// transaction + reusable column mapping presets per
//  + SSE-driven progress feed.
//
// Phase 6 Wave 6 Task 21 — `/orders/views` and `/orders/settings` route
// to the new merchant Custom Views + Order Settings pages. The Custom
// Views page lets merchants save column / filter combinations; Apply
// navigates to `/orders?viewId=:id` so OrdersListPage can preselect.
// The Settings page surfaces auto-sync, fulfillment, auto-cancel,
// shipping carrier and notification email defaults.
//
// Phase 6 Wave 7 Task 22 — `/orders/:id/payment-result` resolves to the
// new `PaymentLandingPage` (gateway-redirect resolution surface for
// Stripe / PayPal sessions). Verifies via 2s polling +
// ORDER_STATUS_CHANGED WS shortcut, with a 30s reconciliation timeout
// . `OrderPaymentPage` (3-method cards + idempotency-key + Pay Later
// toggle + credit-debt warning) is reached from the order-detail "Pay now"
// modal; the `?step=payment` wizard host it also had was removed with
// `OrderCreateWizard` on 2026-09-25.
//
// Phase 6 Wave 7 Task 23 — `/orders/:id/refund` resolves to the new
// `OrdersRefundPage` (merchant-initiated refund flow, wallet-only per
// Reachable via the new "Refund" button on
// OrderDetailPage. Chargeback display lives on the detail page Overview
// tab (read-only — admin-handled)..
//
// Phase 6 Wave 8 Task 25 — Store OAuth connect surface lands at
// `/stores/connect/*`. Hub at `/stores/connect` (6 platform cards),
// platform-specific pages at `/stores/connect/{shopify,woocommerce,
// tiktok}`, and a shared callback handler at `/stores/connect/callback`
// that consumes the one-shot state token and routes to the
// new `/stores/:id` detail page on success. Etsy still ships only the
// "Use Chrome Extension" CTA today; native Etsy + Amazon
// connectors stay "Coming soon" until the dedicated waves land..
//
// Phase 6 Wave 8 Task 26 — `/stores/:id` now resolves to the new
// `StoreDetailPage` (replaces the Wave 1 placeholder stub at
// `@/pages/StoreDetail`). 4 tabs: Overview / Mapping Center / Sync
// Logs / Settings. Tab routing via `?tab=...` query param so deep
// links land on the right tab.
// . Goals.
//
// Phase 6 Wave 9 Task 28 — `/wallet` now resolves to the new
// `WalletOverviewPage` (replaces the Wave 1 placeholder at
// `@/pages/Wallet`). Layout: 5-card KPI grid + Credit envelope panel
// + spending chart (stacked categories + total trend) + low-balance
// alert + sub-tab nav (Transactions / Linked accounts / Bank accounts
// / Topup / Withdraw — Tasks 29 / 30 / 31 wire those panels). Live
// balance refresh on `merchant.${id}.wallet` WS channel.
// Goals.
//
// Phase 6 Wave 9 Task 30 — `/wallet/transactions` now resolves to the
// new `WalletTransactionHistoryPage` (replaces the Wave 1 placeholder
// at `@/pages/WalletTransactions`). Filter chips (9 types
// + Gateway Fee + date range + status), CSV/Excel export
// per-row receipt PDF, and a Send Balance modal (4-step
// wizard). `/wallet/withdraw` resolves to the new
// `WalletWithdrawPage` (bank account list + amount + WithdrawModal
// with 2FA verify). Goals.
//
// Phase 6 Wave 9 Task 31 — `/wallet/linked-accounts` and
// `/wallet/bank-accounts` resolve to the new Linked accounts +
// Bank accounts management pages. Both lazy-loaded
// so each page ships its own chunk.
//
// Phase 6 Wave 8 Task 27 — Cross-store + per-store sync surface:
//   • `/sync-logs` (canonical) + `/stores/sync-logs` (legacy alias) →
//     `SyncLogsAggregationPage` (cross-store aggregation, +
//     The legacy `StoresSyncLogs` lazy import is repointed
//     to the new aggregation page so the Wave 1 placeholder is fully
//     replaced.
//   • `/stores/:id/external-products` → `StoresExternalProductsPage`
//     (per-store imported products view).
//   • `/stores/:id/disconnect` → `StoresDisconnectPage` (red-banner
//     disconnect flow).
//   • `/stores/:id/sync-settings` → `StoresSyncSettingsPage` (advanced
//     ops panel).
//   • `/stores/:id/sync-orders` + `/stores/:id/sync-products` →
//     dedicated trigger pages with narrowed-run filters.
//
// Goals.
//
// vg-binding: PLAN:task-24
// vg-binding: P6.1.D-27
// vg-binding: P6.1.D-28
// vg-binding: P6.1.D-02
// vg-binding: G-12
// vg-binding: G-15
// vg-binding: CONTEXT:decisions
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: PLAN:task-02
// vg-binding: PLAN:task-03
// vg-binding: PLAN:task-08
// vg-binding: PLAN:task-40
// vg-binding: PLAN:task-41
// vg-binding: PLAN:task-43
// vg-binding: P6.D-184
// vg-binding: P6.D-204
// vg-binding: P6.D-222
// vg-binding: G-72
// vg-binding: P6.D-07
// vg-binding: P6.D-08
// vg-binding: P6.D-09
// vg-binding: P6.D-10
// vg-binding: P6.D-11
// vg-binding: P6.D-12
// vg-binding: P6.D-13
// vg-binding: P6.D-14
// vg-binding: G-67
// vg-binding: G-68
// vg-binding: P6.D-118
// vg-binding: P6.D-120
// vg-binding: P6.D-121
// vg-binding: P6.D-174
// vg-binding: P6.D-175
// vg-binding: G-65
// vg-binding: G-66
// vg-binding: PLAN:task-29
// vg-binding: PLAN:task-30
// vg-binding: PLAN:task-31
// vg-binding: PLAN:task-32
// vg-binding: PLAN:task-33
// vg-binding: PLAN:task-34
// vg-binding: PLAN:task-35
// vg-binding: PLAN:task-36
// vg-binding: PLAN:task-37
// vg-binding: PLAN:task-38
// vg-binding: PLAN:task-39
// vg-binding: P6.D-100
// vg-binding: P6.D-101
// vg-binding: P6.D-102
// vg-binding: P6.D-103
// vg-binding: P6.D-105
// vg-binding: P6.D-119
// vg-binding: P6.D-225
// vg-binding: G-62
// vg-binding: G-63
// vg-binding: G-64
// vg-binding: P6.D-80
// vg-binding: P6.D-81
// vg-binding: P6.D-82
// vg-binding: P6.D-83
// vg-binding: P6.D-84
// vg-binding: P6.D-86
// vg-binding: P6.D-87
// vg-binding: P6.D-88
// vg-binding: P6.D-89
// vg-binding: P6.D-90
// vg-binding: P6.D-91
// vg-binding: G-60
// vg-binding: G-61
// vg-binding: P6.D-124
// vg-binding: P6.D-125
// vg-binding: P6.D-126
// vg-binding: P6.D-127
// vg-binding: P6.D-128
// vg-binding: P6.D-129
// vg-binding: P6.D-130
// vg-binding: G-58
// vg-binding: G-59
// vg-binding: P6.D-122
// vg-binding: P6.D-123
// vg-binding: P6.D-131
// vg-binding: P6.D-132
// vg-binding: P6.D-221
// vg-binding: P6.D-249
// vg-binding: G-57
// vg-binding: G-69
// vg-binding: P6.D-66
// vg-binding: G-56
// vg-binding: P6.D-70
// vg-binding: P6.D-245
// vg-binding: G-54
// vg-binding: G-55
// vg-binding: G-52
// vg-binding: G-53
// vg-binding: G-51
// vg-binding: P6.D-65
// vg-binding: P6.D-69
// vg-binding: P6.D-233
// vg-binding: G-49
// vg-binding: G-50
// vg-binding: P6.D-66
// vg-binding: P6.D-67
// vg-binding: P6.D-68
// vg-binding: P6.D-234
// vg-binding: P6.D-244
// vg-binding: G-48
// vg-binding: PLAN:task-10
// vg-binding: PLAN:task-11
// vg-binding: PLAN:task-14
// vg-binding: PLAN:task-15
// vg-binding: PLAN:task-16
// vg-binding: PLAN:task-17
// vg-binding: PLAN:task-18
// vg-binding: PLAN:task-19
// vg-binding: PLAN:task-20
// vg-binding: PLAN:task-21
// vg-binding: PLAN:task-22
// vg-binding: PLAN:task-23
// vg-binding: PLAN:task-25
// vg-binding: PLAN:task-26
// vg-binding: PLAN:task-27
// vg-binding: PLAN:task-28
// vg-binding: P6.D-64
// vg-binding: P6.D-71
// vg-binding: P6.D-72
// vg-binding: P6.D-73
// vg-binding: P6.D-74
// vg-binding: P6.D-75
// vg-binding: P6.D-76
// vg-binding: P6.D-77
// vg-binding: P6.D-78
// vg-binding: P6.D-79
// vg-binding: G-46
// vg-binding: G-47
// vg-binding: P6.D-112
// vg-binding: P6.D-223
// vg-binding: P6.D-237
// vg-binding: G-44
// vg-binding: G-45
// vg-binding: P6.D-107
// vg-binding: P6.D-108
// vg-binding: P6.D-109
// vg-binding: P6.D-110
// vg-binding: P6.D-111
// vg-binding: P6.D-113
// vg-binding: P6.D-114
// vg-binding: P6.D-202
// vg-binding: G-41
// vg-binding: G-42
// vg-binding: G-43
// vg-binding: G-39
// vg-binding: P6.D-59
// vg-binding: P6.D-59a
// vg-binding: P6.D-59b
// vg-binding: P6.D-59c
// vg-binding: P6.D-231
// vg-binding: P6.D-232
// vg-binding: P6.D-239
// vg-binding: P6.D-240
// vg-binding: P6.D-241
// vg-binding: P6.D-242
// vg-binding: G-37
// vg-binding: G-38
// vg-binding: P6.D-62
// vg-binding: P6.D-63
// vg-binding: G-35
// vg-binding: G-36
// vg-binding: P6.D-53
// vg-binding: P6.D-54
// vg-binding: P6.D-56
// vg-binding: P6.D-60
// vg-binding: P6.D-61
// vg-binding: G-34
// vg-binding: P6.D-40
// vg-binding: P6.D-41
// vg-binding: P6.D-42
// vg-binding: P6.D-43
// vg-binding: P6.D-44
// vg-binding: P6.D-45
// vg-binding: P6.D-46
// vg-binding: P6.D-47
// vg-binding: P6.D-48
// vg-binding: P6.D-49
// vg-binding: P6.D-50
// vg-binding: P6.D-243
// vg-binding: G-30
// vg-binding: G-31
// vg-binding: G-32
// vg-binding: G-33
// vg-binding: P6.D-215
// vg-binding: G-29
// vg-binding: P6.D-92
// vg-binding: P6.D-94
// vg-binding: P6.D-98
// vg-binding: P6.D-149
// vg-binding: P6.D-26
// vg-binding: P6.D-200
// vg-binding: P6.D-227
// vg-binding: P6.D-01
// vg-binding: P6.D-02
// vg-binding: P6.D-03
// vg-binding: P6.D-205
// vg-binding: P6.D-22
// vg-binding: P6.D-24
// vg-binding: P6.D-34
// vg-binding: P6.D-51
// vg-binding: P6.D-52
// vg-binding: P6.D-55
// vg-binding: P6.D-57
// vg-binding: P6.D-58
// vg-binding: INTERFACE-STANDARDS:response-envelope
// vg-binding: INTERFACE-STANDARDS:error-shape

import { lazy, Suspense, type ReactElement } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { LoadingShell } from '@/components/LoadingShell';
import { RequireAuth } from '@/lib/auth/RequireAuth';
// Phase 6.1 Wave 5 Task 24 — scope + role guards. ScopeGate swaps the
// KPI-bearing read surfaces (dashboard/orders/products) for a dedicated
// no-team empty state when the caller has no team assignment (D-28b, G-12);
// SettingsRoleGuard sends staff/viewer to /403 on the Members + Teams
// settings surfaces and renders manager read-only (D-27, G-15).
import { ScopeGate } from '@/components/scope/ScopeGate';
import { SettingsRoleGuard } from '@/components/guards/SettingsRoleGuard';

// --- lazy route modules ---------------------------------------------------

// Phase 6 Wave 12 Task 41 — repointed `Dashboard` from the Wave 1
// placeholder `@/pages/Dashboard` to the new full Dashboard surface
// (`@/pages/dashboard/DashboardPage`). Routes `/` + `/dashboard` are
// preserved for deep-link compatibility.
const Dashboard = lazy(() => import('@/pages/dashboard/DashboardPage'));
const Orders = lazy(() => import('@/pages/orders/OrdersListPage'));
const ShipmentTracking = lazy(() => import('@/pages/shipments/ShipmentTrackingPage'));
const OrderDetail = lazy(() => import('@/pages/orders/OrderDetailPage'));
const OrderCreate = lazy(() => import('@/pages/orders/create/OrderCreatePage'));
const OrderImport = lazy(() => import('@/pages/orders/import/ExcelImportPage'));
const OrdersCustomViews = lazy(() => import('@/pages/orders/OrdersCustomViewPage'));
const OrdersSettings = lazy(() => import('@/pages/orders/OrdersSettingsPage'));
const OrderPaymentResult = lazy(() => import('@/pages/orders/payment/PaymentLandingPage'));
const OrdersRefund = lazy(() => import('@/pages/orders/refund/OrdersRefundPage'));

const ProductsCatalog = lazy(() => import('@/pages/ProductsCatalog'));
const MyProducts = lazy(() => import('@/pages/MyProducts'));
const WizardDrafts = lazy(() => import('@/pages/WizardDraftsPage'));
const ProductCreate = lazy(() => import('@/pages/products/wizard/ProductCreateWizard'));
const ProductDetail = lazy(() => import('@/pages/catalog/BlueprintDetailPage'));
// dbg-20260602 — /products/my/:id now reuses the create wizard in edit mode
// (was the tab-based MyProductEditor). The old editor stays importable for
// the legacy `?editor=tabs` escape hatch + rollback.
const MyProductEditWizard = lazy(() => import('@/pages/products/MyProductEditWizard'));
const MyProductEditor = lazy(() => import('@/pages/products/MyProductEditor'));
const ProductSettings = lazy(() => import('@/pages/products/ProductSettingsPage'));
const ArtworkLibrary = lazy(() => import('@/pages/artwork/ArtworkLibraryPage'));
const ArtworkEditor = lazy(() => import('@/pages/artwork/ArtworkEditorPage'));

const Stores = lazy(() => import('@/pages/stores/StoresListPage'));
const StoreDetail = lazy(() => import('@/pages/stores/StoreDetailPage'));
const IntegrationGuide = lazy(() => import('@/pages/stores/IntegrationGuidePage'));
// Task 27 — repointed from the Wave 1 placeholder
// `@/pages/StoresSyncLogs` to the cross-store aggregation page.
const StoresSyncLogs = lazy(() => import('@/pages/stores/SyncLogsAggregationPage'));
const StoresExternalProducts = lazy(() => import('@/pages/stores/StoresExternalProductsPage'));
const StoresDisconnect = lazy(() => import('@/pages/stores/StoresDisconnectPage'));
const StoresSyncSettings = lazy(() => import('@/pages/stores/StoresSyncSettingsPage'));
const StoresSyncOrders = lazy(() => import('@/pages/stores/StoresSyncOrdersPage'));
const StoresSyncProducts = lazy(() => import('@/pages/stores/StoresSyncProductsPage'));
const ConnectStore = lazy(() => import('@/pages/stores/connect/ConnectStorePage'));
const ConnectShopify = lazy(() => import('@/pages/stores/connect/ConnectShopify'));
const ConnectWooCommerce = lazy(() => import('@/pages/stores/connect/ConnectWooCommerce'));
const ConnectTikTok = lazy(() => import('@/pages/stores/connect/ConnectTikTok'));
const ConnectManual = lazy(() => import('@/pages/stores/connect/ConnectManual'));
const OAuthCallback = lazy(() => import('@/pages/stores/connect/OAuthCallbackPage'));

// Task 28 — repointed from the Wave 1 placeholder `@/pages/Wallet` to
// the new full overview surface.
//
// Task 29 — repointed `WalletTopup` from the Wave 1 placeholder
// `@/pages/WalletTopup` to the new full topup surface
// (`@/pages/wallet/topup/WalletTopupPage`). Route /wallet/topup is
// preserved for deep-link compatibility.
const Wallet = lazy(() => import('@/pages/wallet/WalletOverviewPage'));
const WalletTopup = lazy(() => import('@/pages/wallet/topup/WalletTopupPage'));
// Task 30 — added a dedicated `/wallet/withdraw` route. The transactions
// page itself is now mounted by the Wallet hub as a tab; `/wallet/transactions`
// redirects there.
const WalletWithdraw = lazy(() => import('@/pages/wallet/WalletWithdrawPage'));
// Task 31 — Linked accounts + Bank accounts management surfaces.
const WalletLinkedAccounts = lazy(() => import('@/pages/wallet/WalletLinkedAccountsPage'));
const WalletBankAccounts = lazy(() => import('@/pages/wallet/WalletBankAccountsPage'));

// Task 32 — repointed `Invoices` from the Wave 1 placeholder
// `@/pages/Invoices` to the new full standalone Finance entry
// (`@/pages/billing/InvoicesListPage`). Route /billing/invoices is
// preserved for deep-link compatibility. New `/billing` route lands
// the BillingOverviewPage hub (Amendment #1).
//
// Task 33 — repointed `InvoiceDetail` from the Wave 1 placeholder
// `@/pages/InvoiceDetail` to the new full detail page. Added new
// lazy modules for invoice CREATE, PAYMENT, payment history, and
// statements pages. All 5 routes lazy-loaded so each ships its own
// chunk (code splitting).
//
// Task 34 — Adds `/billing/payment-methods` (saved Stripe/PayPal
// management surface) + `/billing/fulfillment-reports` (generated
// fulfillment report list) + `/billing/fulfillment-reports/generate`
// (Generate form with 3s status polling). All 3 routes lazy-loaded
// .
const InvoiceDetail = lazy(() => import('@/pages/billing/InvoiceDetailPage'));
const InvoiceCreate = lazy(() => import('@/pages/billing/InvoiceCreatePage'));
const InvoicePayment = lazy(() => import('@/pages/billing/InvoicePaymentPage'));
const PaymentHistory = lazy(() => import('@/pages/billing/PaymentHistoryPage'));
const Statements = lazy(() => import('@/pages/billing/StatementsPage'));
const FulfillmentReports = lazy(() => import('@/pages/billing/FulfillmentReportsPage'));
const FulfillmentReportsGenerate = lazy(
  () => import('@/pages/billing/FulfillmentReportsGeneratePage'),
);
// Phase 3.5 port (merchant-v3 billing surfaces from legacy apps/merchant):
// billing addresses CRUD, payment requests, billing settings tab suite.
const BillingAddresses = lazy(() => import('@/pages/billing/AddressesPage'));

// Phase 6 Wave 11 Task 38 — `/settings/branding` repointed from the
// Wave 1 placeholder `@/pages/SettingsBranding` to the new full
// 4-tab Branding surface (Brand identity / Thank-you cards / Gift
// messages / Packaging) with inline changelog. Added
// `/settings/notifications` route for the new Notifications preferences
// page. Both lazy-loaded.
//
// Phase 6 Wave 11 Task 39 — Adds the four standalone Branding editor
// surfaces under `/branding/*`. Each
// route ships its own chunk code splitting.
const SettingsBranding = lazy(() => import('@/pages/settings/SettingsBrandingPage'));
const BrandingGiftMessages = lazy(() => import('@/pages/branding/GiftMessagesPage'));
const BrandingCreateGiftMessage = lazy(() => import('@/pages/branding/CreateGiftMessagePage'));
const BrandingThankYouCards = lazy(() => import('@/pages/branding/ThankYouCardsPage'));
const BrandingCreateThankYouCard = lazy(() => import('@/pages/branding/CreateThankYouCardPage'));
// Phase 6 Wave 11 Task 35 — repointed `SettingsProfile` from the Wave 1
// placeholder `@/pages/SettingsProfile` to the new full Profile surface
// at `@/pages/settings/SettingsProfilePage`. Added `SettingsGeneral`
// at `/settings/general` (BillingInfoForm + BillingModeDisplay +
// PreferencesPanel/132/221).
// 2026-06-26 — consolidated legacy-style account-settings (5 icon tabs in one
// card). Replaces the old SettingsProfilePage at /settings/profile.
const AccountSettings = lazy(() => import('@/pages/settings/AccountSettingsPage'));
const SettingsGeneral = lazy(() => import('@/pages/settings/SettingsGeneralPage'));
// Phase 5.2 Task 16 fix (CrossAI iter1 P5.2.D-20) — Partner API Keys tab
// (`/settings/api-keys`), mounting the previously-unreachable
// features/settings/api-keys/ApiKeysSection.tsx.
const SettingsApiKeys = lazy(() => import('@/pages/settings/SettingsApiKeysPage'));
// Phase 6 Wave 11 Task 36 — Security surface (..).
// Each route ships its own chunk code splitting.
// #2594: SecurityPage retired — /settings/security now redirects to
// /settings/profile?tab=security (canonical SecurityTab in the shell).
const SettingsSetup2FA = lazy(() => import('@/pages/settings/Setup2FAPage'));
// Login history (Security → Activity tab). The page existed but was never
// routed, so the SettingsTabs `activity` link (/settings/login-history)
// 404'd (merchant UAT settings-30/31/32).
const SettingsLoginHistory = lazy(() => import('@/pages/settings/LoginHistoryPage'));
const SettingsActiveSessions = lazy(() => import('@/pages/settings/ActiveSessionsPage'));
// Phase 6 Wave 11 Task 37 — Team members management.
// Lazy-loaded so the team table + 3 modals ship as a separate chunk
// (code splitting). Replaces the Wave 1 placeholder at
// `@/pages/Team`.
// Phase 6.1 Wave 5 Task 21 — Members page at the new canonical route
// `/settings/member` (P6.1.D-25). Fully replaces the legacy
// TeamMembersPage (`@/pages/settings/TeamMembersPage`, kept on disk for
// rollback); the old `/settings/team` URL permanently redirects here
// (P6.1.D-26) so bookmarks/muscle-memory keep working.
// vg-binding: PLAN:task-21
// vg-binding: P6.1.D-25
// vg-binding: P6.1.D-26
const SettingsMembers = lazy(() => import('@/pages/settings/MembersPage'));
// vg-binding: P6.1.D-26
// vg-binding: P6.1.D-19
// vg-binding: P6.1.D-04
// vg-binding: P6.1.D-21
// Phase 6.1 Wave 5 Task 23 — store-based Teams management (P6.1.D-26). NEW
// routes `/settings/teams` (list) + `/settings/teams/:id` (detail). Lazy-
// loaded so the teams table + detail tabs + shared modals ship a separate
// chunk (code splitting).
const SettingsTeams = lazy(() => import('@/pages/settings/TeamsPage'));
const SettingsTeamDetail = lazy(() => import('@/pages/settings/TeamDetailPage'));
const Support = lazy(() => import('@/pages/Support'));
const SupportTicketDetail = lazy(() => import('@/pages/SupportTicketDetail'));

// Ticket module moved from /support/* to /tickets/* (dbg-20260706-153740).
// Param-carrying redirect for legacy detail deep links.
function LegacyTicketDetailRedirect(): ReactElement {
  const { id } = useParams();
  return <Navigate to={`/tickets/${id ?? ''}`} replace />;
}

// Phase 6 Wave 12 Task 40 — `/notifications` repointed from the Wave 1
// placeholder `@/pages/Notifications` to the new full Notification
// Center surface (6 tabs + grouped list + bulk Mark All Read + WS
// realtime). New `/notifications/settings` route lands the sibling
// preferences page (re-uses the Wave 11 Task 38 BE endpoint). Both
// lazy-loaded code splitting.
const Notifications = lazy(() => import('@/pages/notifications/NotificationsCenterPage'));
const NotificationsSettings = lazy(() => import('@/pages/notifications/NotificationsSettingsPage'));
// Bug-cluster polish — `/notifications/:id` detail surface for rows
// without an `actionUrl` (otherwise the row clicks were a no-op).
const NotificationDetail = lazy(() => import('@/pages/notifications/NotificationDetailPage'));
// Phase 6 Wave 12 Task 41 — repointed `Onboarding` from the Wave 1
// placeholder `@/pages/Onboarding` to the new 5-step welcome wizard
// (`@/pages/onboarding/OnboardingPage`). Route `/onboarding` preserved
// for deep-link compatibility.
const Onboarding = lazy(() => import('@/pages/onboarding/OnboardingPage'));
const Login = lazy(() => import('@/pages/Login'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
// Phase 6 Wave 12 Task 43 — Reset password (public, no RequireAuth) lands
// at /reset-password?token=… with the new strength meter + token-invalid
// red panel.
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
// Phase 6 Wave 15a Task 54 — public register + verify-email
// flow. Both routes are public (no <RequireAuth>) because the
// account doesn't exist yet (register) / hasn't been activated yet
// (verify-email).
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
// Phase 6 Wave 12 Task 43 — Replace the legacy `@/pages/NotFound` (Wave 1
// stub) with the new centred `errors/NotFoundPage` plus dedicated /403
// and /500 surfaces.
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/errors/ForbiddenPage'));
const ServerErrorPage = lazy(() => import('@/pages/errors/ServerErrorPage'));
const MaintenancePage = lazy(() => import('@/pages/errors/MaintenancePage'));

// --- guard helper ---------------------------------------------------------

function P(el: ReactElement): ReactElement {
  return <RequireAuth>{el}</RequireAuth>;
}

// Phase 6.1 Wave 5 Task 24 — auth + no-team scope gate for the KPI-bearing
// read surfaces (dashboard/orders/products). A no-team member sees the
// dedicated empty state with KPI cards hidden entirely (D-28b, G-12); owner/
// admin + team members fall through to the page.
function PS(el: ReactElement): ReactElement {
  return (
    <RequireAuth>
      <ScopeGate>{el}</ScopeGate>
    </RequireAuth>
  );
}

// Phase 6.1 Wave 5 Task 24 — auth + settings role gate for the Members +
// Teams settings surfaces. staff/viewer → /403; manager → read-only context
// (D-27, G-15, TS-15). Owner/admin get full access.
function PR(el: ReactElement): ReactElement {
  return (
    <RequireAuth>
      <SettingsRoleGuard>{el}</SettingsRoleGuard>
    </RequireAuth>
  );
}

// --- exported router ------------------------------------------------------

export function AppRouter(): JSX.Element {
  return (
    <Suspense fallback={<LoadingShell />}>
      <Routes>
        {/* Public auth routes (no <RequireAuth>). Phase 6 Wave 12 Task
            43 adds /reset-password as the second public auth surface
            (consumes the one-shot token from the email link). */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Default landing — RequireAuth handles unauth → /login redirect.
            Task 24 — PS() wraps these KPI-bearing surfaces in the no-team
            scope gate (D-28b/G-12): a member with no team assignment sees a
            dedicated empty state with KPI cards hidden, not a "0" dashboard. */}
        <Route path="/" element={PS(<Dashboard />)} />
        <Route path="/dashboard" element={PS(<Dashboard />)} />

        {/* Orders */}
        <Route path="/orders" element={PS(<Orders />)} />
        <Route path="/shipments" element={P(<ShipmentTracking />)} />
        <Route path="/orders/create" element={P(<OrderCreate />)} />
        <Route path="/orders/import" element={P(<OrderImport />)} />
        <Route path="/orders/views" element={P(<OrdersCustomViews />)} />
        <Route path="/orders/settings" element={P(<OrdersSettings />)} />
        <Route path="/orders/:id/payment-result" element={P(<OrderPaymentResult />)} />
        <Route path="/orders/:id/refund" element={P(<OrdersRefund />)} />
        <Route path="/orders/:id" element={P(<OrderDetail />)} />

        {/* Products */}
        {/* Task 24 — the Catalog (browse) + My Products list are KPI/list
            read surfaces gated by no-team scope (D-28b/G-12). */}
        <Route path="/products" element={PS(<ProductsCatalog />)} />
        <Route path="/products/my" element={PS(<MyProducts />)} />
        <Route path="/products/my/:id" element={P(<MyProductEditWizard />)} />
        {/* dbg-20260602 — legacy tab editor kept for rollback / comparison */}
        <Route path="/products/my/:id/tabs" element={P(<MyProductEditor />)} />
        <Route path="/products/wizard-drafts" element={P(<WizardDrafts />)} />
        <Route path="/products/create" element={P(<ProductCreate />)} />
        <Route path="/products/settings" element={P(<ProductSettings />)} />
        <Route path="/artwork" element={P(<ArtworkLibrary />)} />
        <Route path="/artwork/:id/edit" element={P(<ArtworkEditor />)} />
        <Route path="/products/artwork" element={P(<ArtworkLibrary />)} />
        <Route path="/products/:id" element={P(<ProductDetail />)} />

        {/* Stores */}
        <Route path="/stores" element={P(<Stores />)} />
        {/* Task 27 — cross-store sync logs aggregation (canonical path
            `/sync-logs` per P6.D-223 + legacy alias `/stores/sync-logs`). */}
        <Route path="/sync-logs" element={P(<StoresSyncLogs />)} />
        <Route path="/stores/sync-logs" element={P(<StoresSyncLogs />)} />
        {/* Connect surface (Task 25) — must be declared before
            `/stores/:id` so the dynamic match does not swallow it. */}
        <Route path="/stores/connect" element={P(<ConnectStore />)} />
        <Route path="/stores/connect/shopify" element={P(<ConnectShopify />)} />
        <Route path="/stores/connect/woocommerce" element={P(<ConnectWooCommerce />)} />
        <Route path="/stores/connect/tiktok" element={P(<ConnectTikTok />)} />
        <Route path="/stores/connect/manual" element={P(<ConnectManual />)} />
        <Route path="/stores/connect/callback" element={P(<OAuthCallback />)} />
        {/* dbg-20260627 — Integration Guide manager. Declared before
            `/stores/:id` so the dynamic match does not swallow it. */}
        <Route path="/stores/integration-guide" element={P(<IntegrationGuide />)} />
        <Route path="/stores/integration-guide/:platform" element={P(<IntegrationGuide />)} />
        {/* Task 27 — per-store admin surfaces. Declared before the
            dynamic `/stores/:id` to keep precedence explicit. */}
        <Route path="/stores/:id/external-products" element={P(<StoresExternalProducts />)} />
        <Route path="/stores/:id/disconnect" element={P(<StoresDisconnect />)} />
        <Route path="/stores/:id/sync-settings" element={P(<StoresSyncSettings />)} />
        <Route path="/stores/:id/sync-orders" element={P(<StoresSyncOrders />)} />
        <Route path="/stores/:id/sync-products" element={P(<StoresSyncProducts />)} />
        <Route path="/stores/:id" element={P(<StoreDetail />)} />

        {/* Wallet */}
        <Route path="/wallet" element={P(<Wallet />)} />
        <Route path="/wallet/topup" element={P(<WalletTopup />)} />
        {/* Also a Wallet tab — see the note on the /billing redirects below. */}
        <Route
          path="/wallet/transactions"
          element={<Navigate to="/wallet?tab=transactions" replace />}
        />
        <Route path="/wallet/withdraw" element={P(<WalletWithdraw />)} />
        <Route path="/wallet/linked-accounts" element={P(<WalletLinkedAccounts />)} />
        <Route path="/wallet/bank-accounts" element={P(<WalletBankAccounts />)} />

        {/* Billing — standalone Finance entry (Amendment #1).
            Task 32 lands `/billing` (overview hub) + repoints
            `/billing/invoices` at the new InvoicesListPage.
            Task 33 adds invoice CREATE / PAYMENT / payment history /
            statements. The /create + /:id/pay routes MUST be declared
            before the dynamic /:id so the dynamic match does not
            swallow them. */}
        {/* Tabs of the Wallet hub, redirected to it.
            These six screens are mounted as tabs on `/wallet`, and each ALSO
            had a standalone route rendering the same component. Opening the
            standalone URL therefore produced the page with no tab strip, no
            way back to Overview, and a breadcrumb that could not name the
            section — the two URLs looked like one screen but behaved as two.
            Redirecting collapses them: one screen, one URL, always in the
            hub. The paths stay so old links and bookmarks keep working. */}
        <Route path="/billing" element={<Navigate to="/wallet?tab=billing" replace />} />
        <Route
          path="/billing/invoices"
          element={<Navigate to="/wallet?tab=invoices" replace />}
        />
        <Route path="/billing/invoices/create" element={P(<InvoiceCreate />)} />
        <Route path="/billing/invoices/:id/pay" element={P(<InvoicePayment />)} />
        <Route path="/billing/invoices/:id" element={P(<InvoiceDetail />)} />
        <Route path="/billing/payment-history" element={P(<PaymentHistory />)} />
        <Route path="/billing/statements" element={P(<Statements />)} />
        {/* Phase 3.5 port — billing addresses, payment requests, settings.
            Static /settings + /addresses + /payment-requests declared before
            the dynamic /invoices/:id block above cannot swallow them (different
            path prefix). */}
        <Route path="/billing/addresses" element={P(<BillingAddresses />)} />
        <Route
          path="/billing/payment-requests"
          element={<Navigate to="/wallet?tab=paymentRequests" replace />}
        />
        <Route
          path="/billing/settings"
          element={<Navigate to="/wallet?tab=billingSettings" replace />}
        />
        {/* Task 34 — Payment methods + Fulfillment reports. The
            `/fulfillment-reports/generate` static path MUST be declared
            before any future dynamic `/fulfillment-reports/:id` so the
            dynamic match does not swallow it. */}
        <Route
          path="/billing/payment-methods"
          element={<Navigate to="/wallet?tab=paymentMethods" replace />}
        />
        <Route
          path="/billing/fulfillment-reports/generate"
          element={P(<FulfillmentReportsGenerate />)}
        />
        <Route path="/billing/fulfillment-reports" element={P(<FulfillmentReports />)} />

        {/* Settings */}
        {/* R9-3 (2026-05-17, BUG-P6-DEBUG-R2-B5) — index redirect.
            User browsed /settings/sessions then pressed browser-back,
            landing on /settings which had no matching route → 404 shell.
            Redirect /settings → /settings/general so the parent path is
            always navigable. `/setting` (missing `s`) is a separate
            typo — covered by the 404 fallback already, but if any
            external link hits it we still 404, which is acceptable. */}
        <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
        <Route path="/settings/branding" element={P(<SettingsBranding />)} />
        <Route path="/settings/profile" element={P(<AccountSettings />)} />
        <Route path="/settings/general" element={P(<SettingsGeneral />)} />
        <Route path="/settings/api-keys" element={P(<SettingsApiKeys />)} />
        {/* Task 36 — Security surface (Password / 2FA / Sessions / Login
            history). The static `/settings/security/2fa` route MUST be
            declared BEFORE `/settings/security` so it is not swallowed
            by the parent index. */}
        <Route path="/settings/security/2fa" element={P(<SettingsSetup2FA />)} />
        {/* #2594 fix: the standalone SecurityPage (old <SettingsTabs/> strip
            with an "api" tab, different chrome) was orphaned from the nav —
            the sidebar only links /settings/profile?tab=security (new
            AccountSettings shell). Redirect the legacy URL to the canonical
            shell tab so there is ONE security surface. Nested routes
            (/security/2fa, /security/sessions) stay — they are sub-pages
            reached from the SecurityTab. */}
        <Route
          path="/settings/security"
          element={<Navigate to="/settings/profile?tab=security" replace />}
        />
        <Route path="/settings/login-history" element={P(<SettingsLoginHistory />)} />
        <Route path="/settings/sessions" element={P(<SettingsActiveSessions />)} />
        {/* Phase 6 Wave 16 Task 80: /settings/security/sessions
            alias — spec navigates here per the security-section
            convention. Reuses the same ActiveSessionsPage rather than
            inlining a panel (one source of truth). */}
        <Route path="/settings/security/sessions" element={P(<SettingsActiveSessions />)} />
        {/* Phase 6.1 Task 21 — Members page at the new canonical route.
            Task 24 — PR() role-gates the surface: staff/viewer typing the URL
            land on /403, manager renders read-only (D-27, G-15, TS-15). */}
        <Route path="/settings/member" element={PR(<SettingsMembers />)} />
        {/* P6.1.D-26 — legacy `/settings/team` permanently redirects to the
            renamed Members page (keeps old bookmarks working). */}
        <Route path="/settings/team" element={<Navigate to="/settings/member" replace />} />
        {/* Phase 6.1 Task 23 — store-based Teams (P6.1.D-26). The static
            `/settings/teams` list is declared before the dynamic
            `/settings/teams/:id` detail so precedence stays explicit.
            Task 24 — PR() role-gates both (staff/viewer → /403, manager
            read-only). */}
        <Route path="/settings/teams" element={PR(<SettingsTeams />)} />
        <Route path="/settings/teams/:id" element={PR(<SettingsTeamDetail />)} />

        {/* Task 39 — Branding standalone editor surfaces (
            P6.D-103 + G-64). The static `/create` paths MUST be
            declared before any dynamic `/:id` siblings (none today
            but kept for future-proofing). */}
        <Route path="/branding/gift-messages/create" element={P(<BrandingCreateGiftMessage />)} />
        <Route path="/branding/gift-messages" element={P(<BrandingGiftMessages />)} />
        <Route
          path="/branding/thank-you-cards/create"
          element={P(<BrandingCreateThankYouCard />)}
        />
        <Route path="/branding/thank-you-cards" element={P(<BrandingThankYouCards />)} />

        <Route path="/tickets" element={P(<Support />)} />
        <Route path="/tickets/:id" element={P(<SupportTicketDetail />)} />
        {/* Legacy /support paths — module moved to /tickets; keep old deep
            links (bookmarks, notification emails) redirecting */}
        <Route path="/support" element={<Navigate to="/tickets" replace />} />
        <Route path="/support/tickets" element={<Navigate to="/tickets" replace />} />
        <Route path="/support/tickets/:id" element={<LegacyTicketDetailRedirect />} />

        {/* Misc */}
        {/* Task 40 — Notifications Center + Settings sibling. The
            static `/notifications/settings` MUST be declared before
            `/notifications` so the dynamic match precedence stays
            explicit (route declaration order is significant in v7). */}
        <Route path="/notifications/settings" element={P(<NotificationsSettings />)} />
        <Route path="/notifications/:id" element={P(<NotificationDetail />)} />
        <Route path="/notifications" element={P(<Notifications />)} />
        <Route path="/onboarding" element={P(<Onboarding />)} />

        {/* Phase 6 Wave 12 Task 43 — Dedicated error surfaces.
            Public, no <RequireAuth>: a 403 in flight may have already
            blown the cookie, and the merchant should still be able to
            read the message + click "Liên hệ Admin". The catch-all 404
            stays last so explicit /403 and /500 routes win. */}
        <Route path="/403" element={<ForbiddenPage />} />
        <Route path="/500" element={<ServerErrorPage />} />
        {/* UAT #2658 — full-screen maintenance surface (public; the api
            client hard-redirects here on 503 MAINTENANCE_MODE_ACTIVE). */}
        <Route path="/maintenance" element={<MaintenancePage />} />

        {/* SPA fallback — Task 43 repointed at the new
            errors/NotFoundPage which centres the surface and adds the
            i18n `errors.404.*` copy. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default AppRouter;
